import axiosInstance from '../axios/axiosInstance';

/**
 * TELV1-503 — transcript-translation coverage + forced retry, proxied by
 * the super-admin backend to the analyzer (`/analyzer/results/*` routes,
 * super-admin token attached server-side).
 *
 * `summary` is the authoritative coverage number: it is scoped to live,
 * digested, eligible (source language ≠ target) conversations — see the
 * TELV1-501 review fixes. The analyzer's raw per-language progress count
 * is deliberately NOT exposed here: it is unscoped (inflated by
 * translations of soft-deleted conversations) and reads misleading.
 */
export interface TranslationSummaryEntry {
  language: string;
  /** Live digested conversations that HAVE a translation in `language`. */
  translated: number;
  /** Live digested conversations eligible for `language`. */
  total: number;
  /**
   * Conversations carrying a recorded translation failure for
   * `language`. Cleared on later success since TELV1-501, but records
   * written before that fix persist after healing — treat as a hint,
   * not a live gap count.
   */
  failed: number;
}

export const translationsApi = {
  getSummary: async (projectId: string): Promise<TranslationSummaryEntry[]> => {
    const { data } = await axiosInstance.get<TranslationSummaryEntry[]>(
      `/analyzer/results/${projectId}/languages/summary`,
    );
    return data;
  },

  /**
   * Force-retry the MISSING translations for one language. `force=true`
   * bypasses the DLQ-attempts cap and the enqueue cooldown; it never
   * re-translates conversations that already have a translation document,
   * so repeated clicks cannot re-spend tokens on healthy data. The
   * analyzer responds as soon as the work is queued (translation itself
   * is async — watch the summary for completion).
   */
  retryMissing: async (projectId: string, language: string): Promise<unknown> => {
    const { data } = await axiosInstance.post<unknown>(
      `/analyzer/results/${projectId}/languages?force=true`,
      { languages: [language] },
    );
    return data;
  },
};
